import { useState, useEffect, useCallback } from 'react'
import { loadTimers, onTimersChanged, isPaused } from '../lib/runningTimers'
import { useVisibleRefresh } from './useVisibleRefresh'

/**
 * Every running timer, kept current. Changes made in this tab or another one
 * arrive straight away; a timer started on another device shows up when this
 * tab comes back into view. `now` ticks once a second while any timer is
 * actually running, so a list of them shares one interval. Pass a
 * contractorId for a contractor's own timers; without one, the studio's.
 */
export function useRunningTimers({ enabled = true, contractorId = null } = {}) {
  const [timers, setTimers] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [now, setNow] = useState(Date.now())

  const refresh = useCallback(async () => {
    if (!enabled) return
    try {
      setTimers(await loadTimers(contractorId ? { contractorId } : undefined))
    } catch (err) {
      console.error('running timers load error:', err)
    } finally {
      setLoaded(true)
    }
  }, [enabled, contractorId])

  useEffect(() => { refresh() }, [refresh])
  useEffect(() => (enabled ? onTimersChanged(refresh) : undefined), [enabled, refresh])
  useVisibleRefresh(refresh, { intervalMs: 60000, enabled })

  const ticking = timers.some(t => !isPaused(t))
  useEffect(() => {
    if (!ticking) return undefined
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])

  return { timers, loaded, now, refresh }
}
